/**
 * PersonFormDialog — create/edit a person (henkilörekisteri, #763/#764)
 *
 * Field rendering, validation and payload building are shared with the inline
 * person creation in the work-order create flow via PersonFormFields (#1002).
 *
 * Quick create (#2904): phone/email are semi-mandatory (ConfirmDialog if
 * either is empty) and a matching phone warns before inserting a duplicate,
 * mirroring the company Y-tunnus warning (#2410). Choosing an existing
 * person calls `onCreated` with that person so a nested caller (party picker)
 * selects it without inserting a row.
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { FormDialog } from '@/components/common/FormDialog';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import {
  PersonFormFields,
  initialPersonFieldsData,
  personFieldsToPayload,
  validatePersonFields,
  type PersonFieldsData,
} from './PersonFormFields';
import { phoneListSearchQuery, phonesMatch } from './phoneMatch';
import { useCreatePerson, usePersons, useUpdatePerson, type Person } from '@/hooks/usePersons';
import { useNotify } from '@/stores/notificationStore';

interface PersonFormDialogProps {
  open: boolean;
  person?: Person | null;
  onClose: () => void;
  onCreated?: (person: Person) => void;
  /** Compact quick-create: essentials plus collapsed address/notes. Default full form. */
  showExtended?: boolean;
  /**
   * Create-mode prefill applied each time the dialog opens, e.g. the name a
   * picker search typed (#3641). Ignored when editing. Keep the reference
   * stable while open — a new object resets the form.
   */
  initialData?: Partial<PersonFieldsData> | undefined;
}

function isContactMissing(data: PersonFieldsData): boolean {
  return !data.phone.trim() || !data.email.trim();
}

export function PersonFormDialog({
  open,
  person,
  onClose,
  onCreated,
  showExtended = true,
  initialData,
}: PersonFormDialogProps) {
  const { t } = useTranslation('persons');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const navigate = useNavigate();
  const isEditing = !!person;

  const [formData, setFormData] = useState<PersonFieldsData>(initialPersonFieldsData);
  const [errors, setErrors] = useState<Partial<Record<keyof PersonFieldsData, string>>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [contactMissingOpen, setContactMissingOpen] = useState(false);
  const [phoneMatches, setPhoneMatches] = useState<Person[] | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const sessionRef = useRef(0);
  const openRef = useRef(open);
  openRef.current = open;

  const phoneSearch = phoneListSearchQuery(formData.phone);
  const personsQuery = usePersons(
    phoneSearch ? { q: phoneSearch } : {},
    { enabled: open && !isEditing && phoneSearch.length >= 5 },
  );

  const createMutation = useCreatePerson();
  const updateMutation = useUpdatePerson();

  const isLive = (session: number) => openRef.current && sessionRef.current === session;

  useEffect(() => {
    sessionRef.current += 1;
    setContactMissingOpen(false);
    setPhoneMatches(null);
    setIsChecking(false);
    if (person) {
      setFormData({
        firstName: person.firstName,
        lastName: person.lastName,
        phone: person.phone ?? '',
        email: person.email ?? '',
        invoiceEmail: person.invoiceEmail ?? '',
        whatsappNumber: person.whatsappNumber ?? '',
        preferredLanguage: person.preferredLanguage,
        streetAddress: person.streetAddress ?? '',
        postalCode: person.postalCode ?? '',
        city: person.city ?? '',
        notes: person.notes ?? '',
        laborRateId: person.laborRateId ?? '',
      });
    } else {
      setFormData({ ...initialPersonFieldsData, ...initialData });
    }
    setErrors({});
    setIsDirty(false);
  }, [person, open, initialData]);

  const validate = (): boolean => {
    const next = validatePersonFields(formData, t);
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleChange = (field: keyof PersonFieldsData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const persist = async (session: number) => {
    const displayName = `${formData.firstName.trim()} ${formData.lastName.trim()}`;
    const data = personFieldsToPayload(formData);

    try {
      if (isEditing && person) {
        await updateMutation.mutateAsync({ id: person.id, data });
        if (!isLive(session)) return;
      } else {
        const created = await createMutation.mutateAsync(data);
        if (!isLive(session)) return;
        onCreated?.(created);
      }
      notify.success(t('saved'), t('savedDesc', { name: displayName }));
      setPhoneMatches(null);
      onClose();
    } catch (error) {
      if (!isLive(session)) return;
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('saveFailed') }));
    }
  };

  const findPhoneMatches = async (): Promise<Person[]> => {
    let people = personsQuery.data?.pages.flatMap((page) => page.data) ?? [];
    if (typeof personsQuery.refetch === 'function') {
      const result = await personsQuery.refetch();
      people = result.data?.pages.flatMap((page) => page.data) ?? people;
    }
    return people.filter((candidate) => phonesMatch(formData.phone, candidate.phone));
  };

  const continueSubmit = async (opts: {
    skipContactConfirm?: boolean;
    skipPhoneConflict?: boolean;
  }) => {
    if (!validate()) return;
    const session = sessionRef.current;

    if (!isEditing && isContactMissing(formData) && !opts.skipContactConfirm) {
      setContactMissingOpen(true);
      return;
    }

    if (!isEditing && !opts.skipPhoneConflict && phoneSearch.length >= 5) {
      setIsChecking(true);
      try {
        const matches = await findPhoneMatches();
        if (!isLive(session)) return;
        if (matches.length > 0) {
          setPhoneMatches(matches);
          return;
        }
      } catch {
        if (!isLive(session)) return;
      } finally {
        if (sessionRef.current === session) setIsChecking(false);
      }
    }

    if (!isLive(session)) return;
    await persist(session);
  };

  const handleSubmit = () => {
    if (contactMissingOpen || phoneMatches != null) return;
    void continueSubmit({});
  };

  const handleUseExisting = (existing: Person) => {
    if (onCreated) {
      onCreated(existing);
    } else {
      navigate(`/parties/persons/${existing.id}`);
    }
    notify.success(
      t('phoneConflict.usingExisting'),
      t('phoneConflict.usingExistingDesc', {
        name: `${existing.firstName} ${existing.lastName}`.trim(),
      }),
    );
    setPhoneMatches(null);
    onClose();
  };

  const isPending = createMutation.isPending || updateMutation.isPending || isChecking;
  const firstMatch = phoneMatches?.[0];

  return (
    <>
      <FormDialog
        open={open}
        onClose={onClose}
        title={isEditing ? t('editPerson') : t('addPerson')}
        isDirty={isDirty}
        onSubmit={handleSubmit}
        isLoading={isPending}
        submitLabel={isEditing ? tc('save') : tc('add')}
        maxWidth="sm"
      >
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <PersonFormFields
              key={`${open}-${person?.id ?? 'new'}`}
              data={formData}
              errors={errors}
              onChange={handleChange}
              showExtended={showExtended}
            />
          </Stack>
        </DialogContent>
      </FormDialog>

      <ConfirmDialog
        open={contactMissingOpen}
        title={t('contactMissing.title')}
        message={t('contactMissing.message')}
        confirmText={t('contactMissing.confirm')}
        onConfirm={() => {
          setContactMissingOpen(false);
          void continueSubmit({ skipContactConfirm: true });
        }}
        onCancel={() => setContactMissingOpen(false)}
      />

      <ResponsiveDialog
        open={phoneMatches != null}
        onClose={() => setPhoneMatches(null)}
        maxWidth="sm"
        fullWidth
        mobileTitle={t('phoneConflict.title')}
      >
        <DialogTitle>{t('phoneConflict.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body2">
              {phoneMatches?.length === 1 && firstMatch
                ? t('phoneConflict.introOne', {
                    phone: formData.phone.trim(),
                    name: `${firstMatch.firstName} ${firstMatch.lastName}`.trim(),
                  })
                : t('phoneConflict.introMany', {
                    phone: formData.phone.trim(),
                    count: phoneMatches?.length ?? 0,
                  })}
            </Typography>
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {(phoneMatches ?? []).map((match) => {
                const details = [match.phone, match.email].filter(Boolean).join(' · ');
                return (
                  <Stack
                    key={match.id}
                    component="li"
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ sm: 'center' }}
                    sx={{ py: 1.25, borderTop: 1, borderColor: 'divider' }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 500 }}>
                        {match.firstName} {match.lastName}
                      </Typography>
                      {details ? (
                        <Typography variant="body2" color="text.secondary">
                          {details}
                        </Typography>
                      ) : null}
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={isPending}
                      onClick={() => handleUseExisting(match)}
                    >
                      {t('phoneConflict.useExisting')}
                    </Button>
                  </Stack>
                );
              })}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setPhoneMatches(null)} disabled={isPending}>
            {tc('cancel')}
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={isPending}
            onClick={() => {
              setPhoneMatches(null);
              void continueSubmit({ skipContactConfirm: true, skipPhoneConflict: true });
            }}
          >
            {t('phoneConflict.createAnyway')}
          </Button>
        </DialogActions>
      </ResponsiveDialog>
    </>
  );
}
